import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/**
 * Durable UI preferences (05 §5.1). Persisted to localStorage under `hadir.ui`.
 * Nothing here is server state and nothing here is sensitive.
 */

export type Lang = 'ar' | 'en';
export type Dir = 'rtl' | 'ltr';
export type ThemePreference = 'light' | 'dark' | 'system';

export interface UiState {
  lang: Lang;
  theme: ThemePreference;
  /** Live page: click on each accepted scan (05 §2.8). Off by default. */
  sound: boolean;
  /** Live page: show university ids instead of names on the projector. */
  hideNames: boolean;

  setLang: (lang: Lang) => void;
  toggleLang: () => void;
  setTheme: (theme: ThemePreference) => void;
  setSound: (sound: boolean) => void;
  setHideNames: (hideNames: boolean) => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      lang: 'ar',
      theme: 'system',
      sound: false,
      hideNames: false,

      setLang: (lang) => set({ lang }),
      toggleLang: () => set({ lang: get().lang === 'ar' ? 'en' : 'ar' }),
      setTheme: (theme) => set({ theme }),
      setSound: (sound) => set({ sound }),
      setHideNames: (hideNames) => set({ hideNames }),
    }),
    {
      name: 'hadir.ui',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        lang: state.lang,
        theme: state.theme,
        sound: state.sound,
        hideNames: state.hideNames,
      }),
    },
  ),
);

export function dirForLang(lang: Lang): Dir {
  return lang === 'ar' ? 'rtl' : 'ltr';
}

/** Resolve `system` against the OS preference; safe when matchMedia is absent. */
export function resolveTheme(preference: ThemePreference): 'light' | 'dark' {
  if (preference !== 'system') return preference;
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
